import type { SelectedProduct } from "@credit-acceptance/step6-bff-client";
import { z } from "zod";

// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy, matching Signers.schema.ts's convention
const REQUIRED_MESSAGE = "This is a required field.";

/** No live vendor-lookup service exists here — a small fixed mock list stands in for `dealerSetupService.fetchAncillaryProductGapVendorMap(...)`. */
export const GAP_VENDOR_OPTIONS = ["GAP-VENDOR", "AMERICAN-GAP", "SAFEGUARD-GAP"] as const;

/** The "Select an Option" entry is itself a real, selectable list item (not a `SelectValue` placeholder) — matches the legacy dropdown's actual shape. Label is translated in the component, alongside the other Form Number options. */
export const GAP_FORM_NUMBER_SELECT_AN_OPTION = "__select_an_option__";
/** Trailing sentinel — counts as a complete (non-blank) selection, same as any real form number. Label is translated in the component. */
export const GAP_FORM_NUMBER_NOT_LISTED = "__not_listed__";

/** Mocked in place of the approved/vendor-scoped forms service — no such service exists here. */
export const GAP_FORM_NUMBER_CODES = ["GAP-FORM-100", "GAP-FORM-200", "GAP-FORM-300"] as const;

// ---------------------------------------------------------------------------
// gapInformationSchema — RHF + Zod for GapInformation.tsx, same pattern as
// VehicleServiceContractInformation.schema.ts. `SelectedProduct` only has
// vendorName/certificateNumber/sellingPrice/addendumNumber/selectedProductName
// — the Addendum State-Prefix, TPAP-vs-non-TPAP vendor split, Term/Coverage,
// Amount Financed, Effective Date, and Form Number have no backend source at
// all, so `tpapGap` (mocked true) and everything it gates are invented, same
// spirit as VSC's `tpapVsc`.
//
// Deliberately NOT modeled (no equivalent concept exists anywhere in this
// codebase, same precedent as VSC): the "match original value on file"
// checks for Selling Price (CAPS/RISC GAP selling price), Term/Coverage
// (deal's RISC Term), and Amount Financed (the $50-tolerance-or-exact-CAPS-
// math dual rule) — all need external CAPS/RISC data and a correction-letter
// feature flag that don't exist here; the AH-deal-status regression check on
// Addendum Number; the Effective Date contractDate-relative range check; and
// every preAppDeal/ecDeal/gapEnable/thirdPartyEligible disable nuance — those
// all collapse to the plain `editable` toggle. The vendor-change listener
// resets Form Number only — not the legacy `!=` reference-comparison bug
// ("fires on every postback"), which the spec itself calls out as a defect,
// not intended behavior.
//
// One difference from VSC: Addendum State-Prefix/Number are required
// *unconditionally* (regardless of tpapGap) — every other field here is only
// required while tpapGap.
// ---------------------------------------------------------------------------
export const gapInformationSchema = z
  .object({
    // Never user-editable and never validated — just reflects whatever drives
    // the TPAP-vs-non-TPAP branching below (see the superRefine).
    tpapGap: z.boolean(),
    // Non-TPAP pure display counterpart to `tpapVendor` — never validated.
    vendorDisplay: z.string(),
    // "Not directly enforced" per spec.
    tpapVendor: z.string(),
    // "Halves of the same addendum number" — required regardless of tpapGap.
    addendumStatePrefix: z.string(),
    addendumNumber: z.string(),
    // CurrencyField's onChange already emits `number | undefined` for an empty
    // input — no emptyStringToUndefined preprocessing needed here.
    sellingPrice: z.number().optional(),
    termCoverage: z.string(),
    amountFinanced: z.number().optional(),
    effectiveDate: z.string(),
    formNumber: z.string(),
  })
  .superRefine((data, context) => {
    if (!data.addendumStatePrefix) {
      context.addIssue({ code: "custom", path: ["addendumStatePrefix"], message: REQUIRED_MESSAGE });
    }
    if (!data.addendumNumber) {
      context.addIssue({ code: "custom", path: ["addendumNumber"], message: REQUIRED_MESSAGE });
    }

    if (!data.tpapGap) {
      return;
    }
    if (data.sellingPrice === undefined) {
      context.addIssue({ code: "custom", path: ["sellingPrice"], message: REQUIRED_MESSAGE });
    }
    if (!data.termCoverage) {
      context.addIssue({ code: "custom", path: ["termCoverage"], message: REQUIRED_MESSAGE });
    }
    if (data.amountFinanced === undefined) {
      context.addIssue({ code: "custom", path: ["amountFinanced"], message: REQUIRED_MESSAGE });
    }
    if (!data.effectiveDate) {
      context.addIssue({ code: "custom", path: ["effectiveDate"], message: REQUIRED_MESSAGE });
    }
    if (!data.formNumber || data.formNumber === GAP_FORM_NUMBER_SELECT_AN_OPTION) {
      context.addIssue({ code: "custom", path: ["formNumber"], message: REQUIRED_MESSAGE });
    }
  });

export type GapInformationInput = z.infer<typeof gapInformationSchema>;

/**
 * Builds RHF defaultValues from the real `SelectedProduct` prop where a real
 * field exists (vendorName/addendumNumber/sellingPrice); everything else
 * (tpapGap, tpapVendor, addendumStatePrefix, term/coverage, amount financed,
 * effective date, form number) is mocked — no backend source exists yet —
 * seeded with valid, non-required-error values so the mocked "Yes" (TPAP)
 * branch is demoable today.
 * @param gap The dashboard response's `SelectedProduct` for GAP, if any.
 * @returns {GapInformationInput} Seed values for `useForm`'s `defaultValues`.
 */
export function buildGapDefaultValues(gap?: SelectedProduct): GapInformationInput {
  return {
    tpapGap: true,
    vendorDisplay: gap?.vendorName ?? "",
    tpapVendor: GAP_VENDOR_OPTIONS[0],
    addendumStatePrefix: "OH",
    addendumNumber: gap?.addendumNumber ?? "ADD-1000",
    sellingPrice: gap?.sellingPrice ?? 900,
    termCoverage: "36",
    amountFinanced: 25_000,
    effectiveDate: "2024-01-15",
    formNumber: GAP_FORM_NUMBER_CODES[0],
  };
}
